import React from 'react';
import FuseAnimate from '@fuse/core/FuseAnimate';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { Grid, Paper, Switch, TextField, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyIcon from '@mui/icons-material/VpnKey';
import CodeIcon from '@mui/icons-material/Code';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import { showSuccessSwal } from 'app/utility/Swalutility';

const VOICES = [
	{ id: 'alloy', label: 'Alloy', description: 'Neutral & balanced' },
	{ id: 'ash', label: 'Ash', description: 'Calm & articulate' },
	{ id: 'ballad', label: 'Ballad', description: 'Soft & melodic' },
	{ id: 'coral', label: 'Coral', description: 'Bright & friendly' },
	{ id: 'echo', label: 'Echo', description: 'Warm & resonant' },
	{ id: 'sage', label: 'Sage', description: 'Thoughtful & steady' },
	{ id: 'shimmer', label: 'Shimmer', description: 'Clear & gentle' },
	{ id: 'verse', label: 'Verse', description: 'Expressive & lively' },
	{ id: 'marin', label: 'Marin', description: 'Smooth & natural' },
	{ id: 'cedar', label: 'Cedar', description: 'Deep & grounded' }
];

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
		padding: theme.spacing(3)
	},
	section: {
		padding: theme.spacing(3),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5)
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		color: theme.palette.text.primary
	},
	sectionTitle: {
		fontWeight: 600
	},
	helperText: {
		color: theme.palette.text.secondary,
		fontSize: 12
	},
	voiceCard: {
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1.5),
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		backgroundColor: theme.palette.background.paper,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
		'&:hover': {
			borderColor: theme.palette.primary.main,
			transform: 'translateY(-1px)'
		}
	},
	voiceCardSelected: {
		borderColor: theme.palette.primary.main,
		borderWidth: 2,
		backgroundColor:
			theme.palette.type === 'dark'
				? 'rgba(255,255,255,0.04)'
				: 'rgba(25, 118, 210, 0.04)'
	},
	voiceLabel: {
		fontWeight: 600,
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	voiceDescription: {
		color: theme.palette.text.secondary,
		fontSize: 12
	},
	checkBadge: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1),
		color: theme.palette.primary.main
	},
	toggleRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: theme.spacing(2)
	},
	toggleLabel: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2
	},
	saveRow: {
		display: 'flex',
		justifyContent: 'flex-end'
	}
}));

function SettingsTab({ settings, updateSettings }) {
	const classes = useStyles();
	const theme = useTheme();

	const onApiKeyChange = e => updateSettings({ apiKey: e.target.value });
	const onPromptChange = e => updateSettings({ systemPrompt: e.target.value });
	const onVoiceSelect = voice => {
		updateSettings({ voice });
		showSuccessSwal(
			'Voice Updated',
			`Voice set to ${voice.charAt(0).toUpperCase() + voice.slice(1)}.`
		);
	};

	const onInterruptionsToggle = () => {
		const newValue = !settings.interruptionsEnabled;
		updateSettings({ interruptionsEnabled: newValue });
		showSuccessSwal(
			'Interruptions Updated',
			newValue ? 'Voice interruptions are now enabled.' : 'Voice interruptions are now disabled.'
		);
	};

	const onSaveClick = () => {
		showSuccessSwal('Settings Saved', 'Your voice agent settings have been saved.');
	};

	return (
		<FusePageCarded
			header={
				<div className="flex flex-1 w-full items-center justify-between flex-wrap">
					<div className="hidden sm:flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<SettingsIcon fontSize="large" />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="flex mx-0 sm:mx-12" variant="h6">
								<span style={{ fontSize: '15px' }}>Voice Agent Settings</span>
							</Typography>
						</FuseAnimate>
					</div>
				</div>
			}
			content={
				<div className={classes.root}>
					<Paper className={classes.section} elevation={0}>
						<div className={classes.sectionHeader}>
							<KeyIcon fontSize="small" />
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								OpenAI API Key
							</Typography>
						</div>
						<TextField
							type="password"
							variant="outlined"
							size="small"
							fullWidth
							placeholder="sk-..."
							value={settings.apiKey || ''}
							onChange={onApiKeyChange}
							inputProps={{ style: { fontSize: 14, color: theme.palette.text.primary } }}
							InputLabelProps={{
								style: { color: theme.palette.text.primary, fontSize: 14 }
							}}
						/>
						<Typography className={classes.helperText}>
							Your key is stored locally and never sent to any server except OpenAI.
						</Typography>
					</Paper>

					<Paper className={classes.section} elevation={0}>
						<div className={classes.sectionHeader}>
							<CodeIcon fontSize="small" />
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								System Prompt
							</Typography>
						</div>
						<FormFieldOmni
							variant="outlined"
							value={settings.systemPrompt || ''}
							onChange={onPromptChange}
							multiline
							rows={6}
							placeholder="Describe how the assistant should behave..."
						/>
						<Typography className={classes.helperText}>
							This prompt defines how the AI behaves. Mention lead capture in the prompt to
							encourage the tool to activate.
						</Typography>
					</Paper>

					<Paper className={classes.section} elevation={0}>
						<div className={classes.sectionHeader}>
							<VolumeUpIcon fontSize="small" />
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								Voice
							</Typography>
						</div>
						<Grid container spacing={2}>
							{VOICES.map(voice => {
								const selected = settings.voice === voice.id;
								return (
									<Grid item xs={12} sm={6} md={4} key={voice.id}>
										<div
											className={`${classes.voiceCard} ${
												selected ? classes.voiceCardSelected : ''
											}`}
											onClick={() => onVoiceSelect(voice.id)}
											role="button"
											tabIndex={0}
											onKeyDown={e => {
												if (e.key === 'Enter' || e.key === ' ') onVoiceSelect(voice.id);
											}}
										>
											{selected && (
												<CheckCircleIcon className={classes.checkBadge} fontSize="small" />
											)}
											<Typography className={classes.voiceLabel}>{voice.label}</Typography>
											<Typography className={classes.voiceDescription}>
												{voice.description}
											</Typography>
										</div>
									</Grid>
								);
							})}
						</Grid>
					</Paper>

					<Paper className={classes.section} elevation={0}>
						<div className={classes.sectionHeader}>
							<RecordVoiceOverIcon fontSize="small" />
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								Voice Interruptions
							</Typography>
						</div>
						<div className={classes.toggleRow}>
							<div className={classes.toggleLabel}>
								<Typography variant="body2" style={{ fontWeight: 500 }}>
									Allow interruptions during AI speech
								</Typography>
								<Typography className={classes.helperText}>
									{settings.interruptionsEnabled
										? 'Enabled — speaking over the AI will interrupt its response.'
										: 'Disabled — the AI will finish its response even if you speak over it.'}
								</Typography>
							</div>
							<Switch
								checked={settings.interruptionsEnabled !== false}
								onChange={onInterruptionsToggle}
								color="primary"
							/>
						</div>
					</Paper>

					<div className={classes.saveRow}>
						<ButtonOmni label="Save Settings" onClick={onSaveClick} />
					</div>
				</div>
			}
		/>
	);
}

export default React.memo(SettingsTab);
